import { t } from "@lingui/macro";
import { CircleNotch, Crown, FilePdf, Sparkle } from "@phosphor-icons/react";
import type { ResumeDto } from "@stylingcv/dto";
import { Button } from "@stylingcv/ui";
import { pageSizeMap } from "@stylingcv/utils";
import { useCallback, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import type { LoaderFunction } from "react-router-dom";
import { Link, redirect, useLoaderData } from "react-router-dom";

import { Icon } from "@/client/components/icon";
import { BottomCTASection, TopBannerCTA } from "@/client/components/marketing/public-resume-cta";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";
import { findResumeById, findResumeByUsernameSlug, usePrintResume } from "@/client/services/resume";
import { forceDownload } from "@/client/utils/download";
import { trackDownloadResume } from "@/client/utils/gtm";

// Component for upgrade message when user is on free plan
const UpgradeMessage = ({ resumeOwnerName }: { resumeOwnerName?: string }) => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
    <div className="container mx-auto px-4 py-16">
      <div className="mx-auto max-w-4xl text-center">
        {/* Header */}
        <div className="mb-12">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-gradient-to-r from-blue-500 to-purple-600 p-4">
              <Crown className="size-12 text-white" />
            </div>
          </div>

          <h1 className="mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-4xl font-bold text-transparent md:text-5xl">
            {t`This Resume is Private`}
          </h1>

          <div className="mb-8 rounded-2xl border border-gray-200 bg-white p-8 shadow-xl dark:border-gray-700 dark:bg-gray-800">
            <div className="mb-4 flex items-center justify-center">
              <Sparkle className="mr-2 size-6 text-yellow-500" />
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                {resumeOwnerName
                  ? t`${resumeOwnerName}'s resume is currently private`
                  : t`This resume is currently private`}
              </h2>
            </div>

            <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-400">
              {t`If you are the owner of this page, please upgrade your account to reactivate your Public Page and share your professional resume with the world.`}
            </p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="mb-12 grid gap-8 md:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-800">
            <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <Crown className="size-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="mb-3 text-xl font-semibold text-gray-800 dark:text-gray-200">
              {t`Public Resume Sharing`}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {t`Share your professional resume with a custom public URL. Available with Basic and Professional plans.`}
            </p>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-800">
            <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <Sparkle className="size-6 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="mb-3 text-xl font-semibold text-gray-800 dark:text-gray-200">
              {t`AI-Powered Builder`}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {t`Create stunning resumes with our advanced AI technology. Get professional results in minutes.`}
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="rounded-2xl bg-gradient-to-r from-blue-500 to-purple-600 p-8 text-white">
          <h2 className="mb-4 text-3xl font-bold">{t`We are the #1 AI Resume Builder`}</h2>
          <p className="mb-8 text-xl text-blue-100">{t`Try our AI Builder Now for FREE`}</p>

          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Link to="/builder/try">
              <Button
                size="lg"
                variant="secondary"
                className="bg-white text-blue-600 hover:bg-gray-100"
              >
                <Sparkle className="mr-2 size-5" />
                {t`Try Free Builder`}
              </Button>
            </Link>

            <Link to="/pricing">
              <Button
                size="lg"
                variant="outline"
                className="border-white text-blue-600 hover:bg-white/10"
              >
                <Crown className="mr-2 size-5" />
                {t`Upgrade Account`}
              </Button>
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <Link
            to="/"
            className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            ← {t`Back to Homepage`}
          </Link>
        </div>
      </div>
    </div>
  </div>
);

export const PublicResumePage = () => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const { printResume, loading } = usePrintResume();
  const loaderData = useLoaderData();
  
  // Debug tracking
  const renderCount = useRef(0);
  renderCount.current++;
  console.log(`[PublicResumePage] 🌐 Render #${renderCount.current}`, { id: loaderData?.id, hasResume: !!loaderData?.data });

  // Check if this is an upgrade message
  if (loaderData?.showUpgradeMessage) {
    return <UpgradeMessage resumeOwnerName={loaderData.resumeOwnerName} />;
  }

  const { id, title, data: resume } = loaderData;
  const format = resume.metadata.page.format as keyof typeof pageSizeMap;
  const templateName = resume.metadata.template as string | undefined;

  // Minimal Screenshot Protection
  useEffect(() => {
    const isProduction = process.env.NODE_ENV === "production";

    // Disable specific keyboard shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      // 1. Print Screen Disabled - Blocks PrintScreen and Alt + PrintScreen
      if (e.key === "PrintScreen") {
        e.preventDefault();
        return false;
      }

      if (e.altKey && e.key === "PrintScreen") {
        e.preventDefault();
        return false;
      }

      // 2. Snipping Tool Disabled - Blocks Windows + Shift + S
      if (e.key === "s" && e.shiftKey && e.metaKey) {
        e.preventDefault();
        return false;
      }

      // 3. View Source Disabled - Blocks Ctrl + U
      if (e.ctrlKey && e.key === "u") {
        e.preventDefault();
        return false;
      }

      // 4. Save Page Disabled - Blocks Ctrl + S
      if (e.ctrlKey && e.key === "s") {
        e.preventDefault();
        return false;
      }
    };

    // 5. Developer Tools Detection - ONLY on production and desktop
    let devToolsInterval: NodeJS.Timeout | null = null;

    if (isProduction) {
      // Mobile detection - skip devtools detection on mobile devices
      const isMobile = () => {
        return (
          window.innerWidth <= 1024 ||
          /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
            navigator.userAgent,
          ) ||
          "ontouchstart" in window ||
          navigator.maxTouchPoints > 0
        );
      };

      // Only run devtools detection on desktop
      if (!isMobile()) {
        const devtools = { open: false };
        const threshold = 300; // Increased threshold for better accuracy

        const detectDevTools = () => {
          // More robust detection - check both dimensions and account for browser chrome
          const heightDiff = window.outerHeight - window.innerHeight;
          const widthDiff = window.outerWidth - window.innerWidth;

          // Only trigger if both dimensions suggest devtools AND the difference is significant
          if (
            (heightDiff > threshold && widthDiff > 50) ||
            (widthDiff > threshold && heightDiff > 50)
          ) {
            if (!devtools.open) {
              devtools.open = true;
              console.clear();
              document.body.innerHTML =
                '<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:Arial;font-size:24px;color:#ff0000;"><Trans><Trans>Developer Tools Detected - Access Denied</Trans></Trans></div>';
            }
          } else {
            devtools.open = false;
          }
        };

        devToolsInterval = setInterval(detectDevTools, 1000); // Reduced frequency
      }
    }

    // Add event listeners
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup function
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (devToolsInterval) {
        clearInterval(devToolsInterval);
      }
    };
  }, []);

  // Use a ref to store the latest resume data to avoid stale closure issues
  const resumeRef = useRef(resume);
  useEffect(() => {
    resumeRef.current = resume;
  }, [resume]);

  // Send resume data to iframe - stable callback that always uses latest resume
  const updateResumeInFrame = useCallback(() => {
    console.log("[PublicResumePage] 📤 Sending SET_RESUME to iframe");
    const message = { type: "SET_RESUME", payload: resumeRef.current };
    frameRef.current?.contentWindow?.postMessage(message, "*");
  }, []);

  // Handle iframe load event
  useEffect(() => {
    const iframe = frameRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      console.log("[PublicResumePage] 🔄 Iframe load event triggered");
      // Small delay to ensure iframe is ready to receive messages
      setTimeout(updateResumeInFrame, 50);
    };

    iframe.addEventListener("load", handleLoad);
    return () => iframe.removeEventListener("load", handleLoad);
  }, [updateResumeInFrame]);

  // Send resume data whenever it changes (iframe already loaded)
  useEffect(() => {
    if (frameRef.current?.contentWindow) {
      updateResumeInFrame();
    }
  }, [resume, updateResumeInFrame]);

  // Handle messages from iframe (height updates, etc.)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      console.log(`[PublicResumePage] 📨 Message from iframe:`, event.data.type);

      if (event.data.type === "PAGE_LOADED" && frameRef.current) {
        frameRef.current.width = event.data.payload.width;
        frameRef.current.height = event.data.payload.height;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const onDownloadPdf = async () => {
    const { url } = await printResume({ id });

    // Force download the PDF instead of opening in new tab
    await forceDownload(url, `${title}-resume.pdf`);

    trackDownloadResume({
      resumeId: id,
      format: "pdf",
      templateName,
      outcome: "success",
    });
  };

  return (
    <div>
      <Helmet>
        <title>
          {title} - {t`Stylion`}
        </title>
      </Helmet>

      {/* Top Banner CTA for visitors */}
      <TopBannerCTA templateName={templateName} />

      <div
        style={{ width: `${pageSizeMap[format].width}mm` }}
        className="relative z-50 mt-12 overflow-hidden rounded shadow-xl sm:mx-auto sm:mb-6 sm:mt-20 print:m-0 print:shadow-none"
      >
        <iframe
          key={`resume-preview-${id}`}
          ref={frameRef}
          title={title}
          src="/artboard/preview"
          style={{ width: `${pageSizeMap[format].width}mm`, overflow: "hidden" }}
        />
      </div>

      {/* Bottom CTA Section */}
      <BottomCTASection templateName={templateName} />

      <div className="hidden flex-col items-center gap-4 py-10 opacity-50 sm:flex print:hidden">
        <ThemeSwitch />
        <Link to="/">
          <Button size="sm" variant="ghost" className="space-x-1.5 text-xs font-normal">
            <span>{t`Built with`}</span>
            <Icon size={12} />
            <span>{t`Stylion`}</span>
          </Button>
        </Link>
      </div>

      <div className="fixed bottom-5 right-5 z-0 hidden sm:block print:hidden">
        <Button
          variant="ghost"
          className="inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          onClick={onDownloadPdf}
        >
          {" "}
          Download PDF
          {loading ? <CircleNotch size={20} className="animate-spin" /> : <FilePdf size={20} />}
        </Button>
      </div>
    </div>
  );
};

export const publicLoader: LoaderFunction<ResumeDto> = async ({ params }) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const username = params.username!;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const slug = params.slug!;

    return await queryClient.fetchQuery({
      queryKey: ["resume", { username, slug }],
      queryFn: () => findResumeByUsernameSlug({ username, slug }),
    });
  } catch {
    return redirect("/");
  }
};

export const publicResumeByIdLoader: LoaderFunction<ResumeDto> = async ({ params }) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const id = params.id!;

    const resume = await queryClient.fetchQuery({
      queryKey: ["resume", { id }],
      queryFn: () => findResumeById({ id }),
    });

    // Check if resume is public
    if (resume.visibility !== "public") {
      return redirect("/");
    }

    // Fetch the resume owner's subscription tier
    try {
      const userResponse = await axios.get(`/user/${resume.userId}/subscription-info`);
      const userSubscriptionTier = userResponse.data?.subscriptionTier || "FREE";

      // Check if user can share public resumes (Basic, Professional, Enterprise)
      if (userSubscriptionTier === "FREE") {
        // Get user's name from resume data for personalized message
        const resumeOwnerName = resume.data.basics.name || null;

        return {
          showUpgradeMessage: true,
          resumeOwnerName,
        };
      }
    } catch (error) {
      // If we can't fetch user subscription info, assume they can't share
      console.error("Failed to fetch user subscription info:", error);
      return {
        showUpgradeMessage: true,
        resumeOwnerName: null,
      };
    }

    return resume;
  } catch {
    return redirect("/");
  }
};
